import React, { useCallback, useEffect, useState } from 'react';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import List from './List';
import useTranslation from "~/app/hooks/useTranslation";
import { useStoreActions, useStoreState } from '~/store';
import Header from './Header';
import { useHistory, useParams } from 'react-router-dom';
import { IssueDetails, Machine, Service } from "~/types";
import EmptyMessage from './EmptyMessage'
import AddIssue from '~/issues/Add';
import classes from './style.module.scss';
import { route } from '~/app/helpers/route';
import { ROUTES } from '~/app/Router';
import { ServiceStatuses } from '~/app/data/statuses/services';
import CompleteServiceDialog from '../CompleteServiceDialog';
import CloseServiceDialog from '../CloseServiceDialog';
import { SOURCE_ISSUE_TYPE } from '~/app/data/statuses/issue';

export type ServiceChecklistComponent = React.FC<Pick<any, 'onSelect'>>

const ServiceChecklist: ServiceChecklistComponent = () => {
  const { t } = useTranslation('serviceChecklist');

  const { setData } = useAppHeader();
  const { id, serviceId } = useParams<{ id: Machine['id'], serviceId: Service['id'] }>();
  const machine = useStoreState(state => state.machine.data);
  const serviceIntervalId = useStoreState(state => state.service.data.serviceIntervalId);
  const service = useStoreState(state => state.service.data);
  const checks = useStoreState(state => state.service.data.checklist);
  const serviceChecklistUpdating = useStoreState(state => state.service.serviceChecklistUpdating);
  const updateCheckData = useStoreActions(actions => actions.serviceCheck.updateCheckData);
  const currentCheck = useStoreState(state => state.serviceCheck.currentCheck);
  const updateCurrentCheck = useStoreActions(actions => actions.serviceCheck.updateCurrentCheck);
  const fetchServiceChecklist = useStoreActions(actions => actions.service.fetchServiceChecklist);
  const hasSimplifiedWorkorder = useStoreState(state => state.service.hasSimplifiedWorkorder ?? false);
  const netWorkStatus = useStoreState(state => state.app.offline.status);
  const checksData = useStoreState(state => state.serviceCheck.checksData);
  const [openAddIssueDialog, setAddIssueDialog] = useState<boolean>(false);
  const history = useHistory();

  usePageTitle(t('title'));

  useEffect(() => {
    setData({
      title: t('title'),
      subTitle: (machine.intern + " - " + machine.name).toUpperCase(),
      backLink: history.length > 1 ? history.goBack : route(ROUTES.projects),
      showUser: false,
      machineId: '',
      projectId: '',
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      search: false
    });
  }, [setData, machine, t, id, serviceId, history]);

  useEffect(() => {
    if (!checks.length && serviceIntervalId) {
      fetchServiceChecklist({
        serviceId: serviceId,
        serviceIntervalId: serviceIntervalId
      });
    }
  }, [checks.length, fetchServiceChecklist, serviceId, serviceIntervalId])

  const toggleAddIssueDialog = useCallback(() => {
    openAddIssueDialog && updateCurrentCheck(null);
    setAddIssueDialog(!openAddIssueDialog);
  }, [setAddIssueDialog, openAddIssueDialog, updateCurrentCheck]);

  const changeStatus = useCallback((check, status) => {
    updateCurrentCheck(check);
    if (status === 0 && !check.issues.length) {
      toggleAddIssueDialog();
    } else {
      check.status = status;
      updateCheckData({ ...check });
      updateCurrentCheck(null);
    }
  }, [toggleAddIssueDialog, updateCheckData, updateCurrentCheck]);

  const handleIssueSave = useCallback((id: IssueDetails['id']) => {
    if (currentCheck) {
      currentCheck.status = 0;
      currentCheck.issues = [...currentCheck.issues, id];
      updateCurrentCheck(currentCheck);
      updateCheckData({ ...currentCheck });
    }
  }, [currentCheck, updateCurrentCheck, updateCheckData]);

  if (!checksData.length || serviceChecklistUpdating) {
    return <EmptyMessage />;
  }

  return (
    <>
      <div className={false ? classes['disabled'] : ''}>
        <Header />
        <List list={checksData} onSelect={changeStatus} />
      </div>
      <AddIssue open={openAddIssueDialog} onClose={toggleAddIssueDialog} type={'issue'} sourceTypeId={SOURCE_ISSUE_TYPE.SERVICE_CHECKLIST_FELT_APP} onSave={handleIssueSave} />
      {netWorkStatus === 0 && (
        <>
          {(service.status === ServiceStatuses.TRIGGERED && hasSimplifiedWorkorder === true) && <CloseServiceDialog />}
          {(service.status === ServiceStatuses.INPROGRESS && service.workOrderNo) && <CompleteServiceDialog />}
        </>
      )}
    </>
  );
};

export default ServiceChecklist
